import type { CommentsSnapshot, MetaSnapshot, SearchIndexSnapshot, TimelineSnapshot, TrendsSnapshot } from './types';

const DEFAULT_BASE_URL = '/data';
const JSON_HEADERS = { Accept: 'application/json' };

export function getDataBaseUrl(): string {
  const value = import.meta.env.VITE_DATA_BASE_URL as string | undefined;
  if (!value || value.trim() === '') return DEFAULT_BASE_URL;
  return value.replace(/\/$/, '');
}

interface SnapshotFetchOptions {
  version?: string;
  bustCache?: boolean;
}

function buildSnapshotUrl(path: string, options: SnapshotFetchOptions = {}): string {
  const base = getDataBaseUrl();
  const url = new URL(`${base}/${path}`, window.location.href);
  if (options.version) url.searchParams.set('v', options.version);
  if (options.bustCache) url.searchParams.set('_refresh', String(Date.now()));
  return url.toString();
}

async function fetchJson<T>(path: string, options: SnapshotFetchOptions = {}): Promise<T> {
  const url = buildSnapshotUrl(path, options);
  const res = await fetch(url, {
    headers: JSON_HEADERS,
    cache: options.bustCache ? 'reload' : 'default'
  });
  if (!res.ok) {
    throw new Error(`failed to fetch ${url}: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export async function fetchMeta(options: { force?: boolean } = {}): Promise<MetaSnapshot> {
  return fetchJson<MetaSnapshot>('meta.json', { bustCache: options.force });
}

export async function fetchAllSnapshots(options: { force?: boolean; meta?: MetaSnapshot } = {}): Promise<{
  meta: MetaSnapshot;
  timeline: TimelineSnapshot;
  trends: TrendsSnapshot;
  comments: CommentsSnapshot;
  searchIndex: SearchIndexSnapshot;
}> {
  const meta = options.meta ?? await fetchMeta({ force: options.force });
  const fileOptions = { version: meta.updatedAt, bustCache: options.force };
  const [timeline, trends, comments, searchIndex] = await Promise.all([
    fetchJson<TimelineSnapshot>('timeline.json', fileOptions),
    fetchJson<TrendsSnapshot>('trends.json', fileOptions),
    fetchJson<CommentsSnapshot>('comments.json', fileOptions),
    fetchJson<SearchIndexSnapshot>('search-index.json', fileOptions)
  ]);
  return { meta, timeline, trends, comments, searchIndex };
}
